import { forwardRef, useCallback, useState, useMemo, memo, useRef } from 'react';
import { cx } from '@trinity/chat-components/utils';
import { FocusableAnimation } from '../Focusable';
import type { TimeInputProps, InputRefObject, TimeInputValue } from './interfaces';
import styles from './styles/style.module.css';

const TimeInput = ({
  autoComplete = 'off',
  autoFocus = false,
  className,
  classes = {},
  focusableClasses = {},
  invalid = false,
  invalidClassName,
  onBlur,
  onFocus,
  readOnly,
  rootClassName,
  value,
  onChange,
  ...inputProps
}: TimeInputProps, ref: InputRefObject) => {
  const [focused, setFocus] = useState(autoFocus);
  const hoursRef = useRef<HTMLInputElement>(null);
  const minutesRef = useRef<HTMLInputElement>(null);
  const secondsRef = useRef<HTMLInputElement>(null);

  const valueChange = useCallback((key: keyof TimeInputValue) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const computedValue = {
        hours: +hoursRef.current.value,
        minutes: +minutesRef.current.value,
        seconds: +secondsRef.current.value,
      };
      onChange?.({
        ...computedValue,
        [key]: +e.target.value,
      });
    };
  }, [onChange]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setFocus(false);
    onBlur?.(e);
  }, [onBlur]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setFocus(true);
    onFocus?.(e);
  }, [onFocus]);

  const invalidClasses = useMemo(() => cx(
    invalid && cx(invalidClassName, classes.invalid, focusableClasses.invalid),
  ), [invalid, invalidClassName, classes.invalid, focusableClasses.invalid]);

  const rootClasses = useMemo(() => cx(styles.timeRoot, rootClassName, classes.root), [rootClassName, classes.root]);
  const inputClasses = useMemo(() => cx(styles.timeInput, className, classes.input), [className, classes.input]);

  return (
    <div className={rootClasses}>
      <input
        {...inputProps}
        autoComplete={autoComplete}
        className={inputClasses}
        onBlur={handleBlur}
        onFocus={handleFocus}
        type='number'
        pattern="[0-9][0-9]"
        max={23}
        min={0}
        onChange={valueChange('hours')}
        value={formatNumberDisplay(value?.hours)}
        ref={hoursRef} />
      :
      <input
        {...inputProps}
        autoComplete={autoComplete}
        className={inputClasses}
        onBlur={handleBlur}
        onFocus={handleFocus}
        type='number'
        pattern="[0-9][0-9]"
        max={59}
        min={0}
        onChange={valueChange('minutes')}
        value={formatNumberDisplay(value?.minutes)}
        ref={minutesRef} />
      :
      <input
        {...inputProps}
        autoComplete={autoComplete}
        className={inputClasses}
        onBlur={handleBlur}
        onFocus={handleFocus}
        type='number'
        pattern="[0-9][0-9]"
        max={59}
        min={0}
        onChange={valueChange('seconds')}
        value={formatNumberDisplay(value?.seconds)}
        ref={secondsRef} />
      <FocusableAnimation
        classes={{
          invalid: invalidClasses,
          focused: focusableClasses.focused,
          root: focusableClasses.root,
        }}
        focused={focused}
        readOnly={readOnly} />
    </div>
  );
};

function formatNumberDisplay(num: number) {
  //Maybe handle rounding here too
  return num < 10 ? `0${num}` : num;
}

const TimeRef = memo(forwardRef<HTMLInputElement, TimeInputProps>(TimeInput));

export { TimeRef as TimeInput };
export default TimeRef;